import { useMemo } from 'react';
import { DataTable, EmptyState, Tabs } from '@/components/UI';
import type { ColumnDef } from '@/components/UI/dataTableConfig';
import type { PerformanceDay, PerformanceTrade } from '../performanceTypes';
import {
    formatDays,
    formatRatePercent,
    formatRatio,
    formatSignedCurrency,
    formatSignedPercent,
    signedToneClass,
    thresholdToneClass
} from '../performanceFormat';
import {
    BREAKDOWN_TABS,
    buildPeriodBreakdownRows,
    buildTradeBreakdownRows,
    type BreakdownTab,
    type PerformanceBasis,
    type PeriodBreakdownRow,
    type StrategyScope,
    type TradeBreakdownRow
} from '../performanceUtils';

interface PerformanceBreakdownTabsProps {
    days: PerformanceDay[];
    trades: PerformanceTrade[];
    tab: BreakdownTab;
    onTabChange: (tab: BreakdownTab) => void;
    basis: PerformanceBasis;
    scope: StrategyScope;
    today: string;
    symbolByEntityId: Map<number, string>;
}

const periodColumns = (firstHeader: string, showReturn: boolean): ColumnDef<PeriodBreakdownRow>[] => [
    { accessorKey: 'label', header: firstHeader, minSize: 140, size: 180, maxSize: 240 },
    {
        accessorKey: 'pnl',
        header: 'P&L',
        minSize: 100,
        size: 120,
        maxSize: 150,
        cell: ({ row }) => (
            <span className={`font-semibold ${signedToneClass(row.original.pnl)}`}>{formatSignedCurrency(row.original.pnl)}</span>
        )
    },
    ...(showReturn
        ? [
              {
                  accessorKey: 'returnPercent',
                  header: 'Return',
                  minSize: 80,
                  size: 90,
                  maxSize: 110,
                  cell: ({ row }) => (
                      <span className={signedToneClass(row.original.returnPercent)}>{formatSignedPercent(row.original.returnPercent)}</span>
                  )
              } satisfies ColumnDef<PeriodBreakdownRow>
          ]
        : []),
    { accessorKey: 'activeDays', header: 'Active days', minSize: 90, size: 100, maxSize: 120 },
    {
        id: 'greenRed',
        header: 'Green / red',
        minSize: 90,
        size: 100,
        maxSize: 120,
        enableSorting: false,
        cell: ({ row }) => (
            <span>
                <span className="text-profit-green">{row.original.greenDays}</span> /{' '}
                <span className="text-red-500">{row.original.redDays}</span>
            </span>
        )
    },
    { accessorKey: 'closedCount', header: 'Closed', minSize: 70, size: 80, maxSize: 100 },
    {
        accessorKey: 'winRate',
        header: 'Win rate',
        minSize: 80,
        size: 90,
        maxSize: 110,
        cell: ({ row }) => (
            <span className={thresholdToneClass(row.original.winRate, 50)}>{formatRatePercent(row.original.winRate, 0)}</span>
        )
    },
    {
        accessorKey: 'realizedPnl',
        header: 'Realized',
        minSize: 100,
        size: 120,
        maxSize: 150,
        cell: ({ row }) =>
            row.original.closedCount ? (
                <span className={signedToneClass(row.original.realizedPnl)}>{formatSignedCurrency(row.original.realizedPnl)}</span>
            ) : (
                '—'
            )
    }
];

const PerformanceBreakdownTabs = ({
    days,
    trades,
    tab,
    onTabChange,
    basis,
    scope,
    today,
    symbolByEntityId
}: PerformanceBreakdownTabsProps) => {
    const periodRows = useMemo(
        () =>
            tab === 'month' || tab === 'week' || tab === 'weekday'
                ? buildPeriodBreakdownRows({ days, trades, tab, basis, scope, today })
                : [],
        [days, trades, tab, basis, scope, today]
    );

    const tradeRows = useMemo(
        () => (tab === 'exitReason' || tab === 'instrument' ? buildTradeBreakdownRows({ trades, tab, scope, today }) : []),
        [trades, tab, scope, today]
    );

    const tradeColumns = useMemo<ColumnDef<TradeBreakdownRow>[]>(
        () => [
            { accessorKey: 'label', header: tab === 'exitReason' ? 'Exit reason' : 'Instrument', minSize: 150, size: 190, maxSize: 260 },
            { accessorKey: 'tradeCount', header: 'Trades', minSize: 70, size: 80, maxSize: 100 },
            {
                accessorKey: 'winRate',
                header: 'Win rate',
                minSize: 80,
                size: 90,
                maxSize: 110,
                cell: ({ row }) => (
                    <span className={thresholdToneClass(row.original.winRate, 50)}>{formatRatePercent(row.original.winRate, 0)}</span>
                )
            },
            {
                accessorKey: 'netProfit',
                header: 'Realized P&L',
                minSize: 110,
                size: 130,
                maxSize: 160,
                cell: ({ row }) => (
                    <span className={`font-semibold ${signedToneClass(row.original.netProfit)}`}>
                        {formatSignedCurrency(row.original.netProfit)}
                    </span>
                )
            },
            {
                accessorKey: 'sharePercent',
                header: () => <span title="Share of the absolute realized P&L across all groups">Share</span>,
                minSize: 70,
                size: 80,
                maxSize: 100,
                cell: ({ row }) => (row.original.sharePercent === null ? '—' : `${row.original.sharePercent.toFixed(0)}%`)
            },
            {
                accessorKey: 'averageProfitPercentage',
                header: 'Avg trade',
                minSize: 80,
                size: 95,
                maxSize: 120,
                cell: ({ row }) => (
                    <span className={signedToneClass(row.original.averageProfitPercentage)}>
                        {formatSignedPercent(row.original.averageProfitPercentage, 1)}
                    </span>
                )
            },
            {
                accessorKey: 'averageHoldDays',
                header: 'Avg hold',
                minSize: 75,
                size: 85,
                maxSize: 110,
                cell: ({ row }) => formatDays(row.original.averageHoldDays)
            },
            {
                accessorKey: 'profitFactor',
                header: 'Profit factor',
                minSize: 90,
                size: 100,
                maxSize: 120,
                cell: ({ row }) => (
                    <span className={thresholdToneClass(row.original.profitFactor, 1)}>{formatRatio(row.original.profitFactor)}</span>
                )
            },
            {
                id: 'best',
                header: 'Best',
                minSize: 110,
                size: 130,
                maxSize: 160,
                enableSorting: false,
                cell: ({ row }) =>
                    row.original.bestTrade ? (
                        <span className="text-profit-green">
                            {symbolByEntityId.get(row.original.bestTrade.entityId) ?? `#${row.original.bestTrade.entityId}`}{' '}
                            {formatSignedCurrency(row.original.bestTrade.profit, 0)}
                        </span>
                    ) : (
                        '—'
                    )
            },
            {
                id: 'worst',
                header: 'Worst',
                minSize: 110,
                size: 130,
                maxSize: 160,
                enableSorting: false,
                cell: ({ row }) =>
                    row.original.worstTrade ? (
                        <span className="text-red-500">
                            {symbolByEntityId.get(row.original.worstTrade.entityId) ?? `#${row.original.worstTrade.entityId}`}{' '}
                            {formatSignedCurrency(row.original.worstTrade.profit, 0)}
                        </span>
                    ) : (
                        '—'
                    )
            }
        ],
        [symbolByEntityId, tab]
    );

    const content =
        tab === 'exitReason' || tab === 'instrument' ? (
            tradeRows.length ? (
                <DataTable search={false} sort striped columns={tradeColumns} data={tradeRows} getRowId={(row) => row.key} />
            ) : (
                <EmptyState size="sm" title="No closed trades in this period" />
            )
        ) : periodRows.length ? (
            <DataTable
                search={false}
                sort
                striped
                columns={periodColumns(
                    tab === 'month' ? 'Month' : tab === 'week' ? 'Week' : 'Weekday',
                    scope === null && tab !== 'weekday'
                )}
                data={periodRows}
                getRowId={(row) => row.key}
            />
        ) : (
            <EmptyState size="sm" title="No days in this period" />
        );

    return (
        <Tabs
            smallTabs
            activeTab={tab}
            contentClassName="mt-3"
            tabs={BREAKDOWN_TABS.map((entry) => ({ id: entry.key, label: entry.label, content: entry.key === tab ? content : null }))}
            onTabChange={(id) => onTabChange(id as BreakdownTab)}
        />
    );
};

export default PerformanceBreakdownTabs;
